import { Fragment, useMemo, useState, type ComponentType, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded'
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import FlagRoundedIcon from '@mui/icons-material/FlagRounded'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import LocalFloristRoundedIcon from '@mui/icons-material/LocalFloristRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded'
import SelfImprovementRoundedIcon from '@mui/icons-material/SelfImprovementRounded'
import SpaRoundedIcon from '@mui/icons-material/SpaRounded'
import StarsRoundedIcon from '@mui/icons-material/StarsRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import type { ActivityTrackId, CosmeticRarity, SpinTier } from '../../types/schema'
import {
  ACTIVITY_TIER_TICKET_REWARDS,
  ACTIVITY_TRACKS,
  ACTIVITY_TRACK_IDS,
  LOGIN_STREAK_MILESTONES,
  LOGIN_TOTAL_MILESTONES,
  SPIN_TIERS,
  SPIN_TIER_RARITY,
  TRACK_TIER_TICKET_REWARDS,
  resolveTrackLevel,
  type ActivityTrackDefinition,
} from '../../utils/gamificationRegistry'
import { useGamification } from '../../context/useGamification'
import { Button, DisplayCard, type DisplayCardState, FilterChip } from '../Common'
import { Loader } from '../Common/Loader'
import { SpinWheelModal } from './SpinWheelModal'
import { useNls } from '../../nls/nlsProvider'
import {
  getSpinTierLabel,
  getTrackFilterLabel,
  type ExperienceNlsT,
} from '../ExperienceNls'

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const pct = (value: number, of: number) => (of <= 0 ? 100 : Math.min(100, Math.max(0, (value / of) * 100)))

const TIER_ICON: Record<SpinTier, ComponentType<SvgIconProps>> = {
  LUCKY_SPIN: LocalFloristRoundedIcon,
  MEGA_SPIN: DiamondRoundedIcon,
  GIGA_SPIN: AutoAwesomeRoundedIcon,
}

const TIER_ACCENT: Record<SpinTier, string> = {
  LUCKY_SPIN: 'var(--tier-lucky)',
  MEGA_SPIN: 'var(--tier-mega)',
  GIGA_SPIN: 'var(--tier-giga)',
}

const RARITY_MODIFIER: Record<CosmeticRarity, string> = {
  COMMON: 'is-common',
  RARE: 'is-rare',
  EPIC: 'is-epic',
  LEGENDARY: 'is-legendary',
}

/** Rarity implied by the highest tier the user has already reached (0..4). */
const rarityForLevel = (level: number): CosmeticRarity => {
  if (level >= 4) return 'LEGENDARY'
  if (level >= 3) return 'EPIC'
  if (level >= 1) return 'RARE'
  return 'COMMON'
}

const TRACK_ICON: Record<ActivityTrackDefinition['iconKey'], ComponentType<SvgIconProps>> = {
  flag: FlagRoundedIcon,
  target: GpsFixedRoundedIcon,
  branch: HubRoundedIcon,
  checkbox: PlaylistAddCheckRoundedIcon,
  trophy: EmojiEventsRoundedIcon,
  'check-circle': CheckCircleRoundedIcon,
  workspace: TaskAltRoundedIcon,
  stars: StarsRoundedIcon,
  'ai-spark': BoltRoundedIcon,
  'ai-refine': PsychologyRoundedIcon,
  focus: SelfImprovementRoundedIcon,
  lucky: SpaRoundedIcon,
  mega: DiamondRoundedIcon,
  giga: AutoAwesomeRoundedIcon,
}

const lockedTicketsCount = (bucket: Partial<Record<SpinTier, number>>): number => {
  let total = 0
  for (const value of Object.values(bucket)) total += value ?? 0
  return total
}

const summarizeLockedTickets = (bucket: Partial<Record<SpinTier, number>>, t: ExperienceNlsT): string => {
  const parts: string[] = []
  for (const tier of ['LUCKY_SPIN', 'MEGA_SPIN', 'GIGA_SPIN'] as SpinTier[]) {
    const count = bucket[tier] ?? 0
    if (count > 0) parts.push(`${count} ${getSpinTierLabel(tier, t)}`)
  }
  return parts.join(' · ')
}

const LockedTicketsInline = ({ bucket }: { bucket: Partial<Record<SpinTier, number>> }) => {
  const entries = (['LUCKY_SPIN', 'MEGA_SPIN', 'GIGA_SPIN'] as SpinTier[])
    .map((tier) => ({ tier, count: bucket[tier] ?? 0 }))
    .filter((entry) => entry.count > 0)
  if (entries.length === 0) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {entries.map((entry, index) => {
        const Icon = TIER_ICON[entry.tier]
        return (
          <Fragment key={entry.tier}>
            {index > 0 ? <span aria-hidden="true"> · </span> : null}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              {entry.count} <Icon fontSize="inherit" />
            </span>
          </Fragment>
        )
      })}
    </span>
  )
}

/** SpinTier promised by the next tier crossing, for the "next reward" chip. */
const nextRewardTier = (trackId: ActivityTrackId, level: number): SpinTier | undefined => {
  const nextTierNo = level + 1
  if (nextTierNo > 4) return undefined
  const override = TRACK_TIER_TICKET_REWARDS[trackId]
  if (override && override[nextTierNo]) return override[nextTierNo]
  return ACTIVITY_TIER_TICKET_REWARDS[nextTierNo]
}

// ---------------------------------------------------------------------------
// Progression Hero
// ---------------------------------------------------------------------------

const ProgressionHero = ({
  level,
  currentXp,
  xpNeeded,
  streak,
  equippedTitle,
  nextUnlock,
  t,
}: {
  level: number
  currentXp: number
  xpNeeded: number
  streak: number
  equippedTitle: string
  nextUnlock: { title: string; tier: SpinTier } | null
  t: ExperienceNlsT
}) => {
  const filled = pct(currentXp, xpNeeded)
  return (
    <motion.section
      className="achHero"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="achHeroTop">
        <div className="achLevelBadge" aria-label={t('NLSExperienceLevelAria', { level })}>
          <div className="achLevelBadgeInner">
            <small>{t('NLSExperienceLevelAbbrev')}</small>
            <b>{level}</b>
          </div>
        </div>

        <div className="achHeroCore">
          <div className="achHeroTitle">
            <span>{t('NLSExperienceProgressionTitle')}</span>
            <span className="achEquippedTitle">· {equippedTitle}</span>
          </div>
          <div className="achXpBeam" role="progressbar" aria-valuemin={0} aria-valuemax={xpNeeded} aria-valuenow={currentXp}>
            <motion.div
              className="achXpBeamFill"
              initial={{ width: 0 }}
              animate={{ width: `${filled}%` }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <div className="achXpMeta">
            <span>
              <strong>{currentXp}</strong> / {xpNeeded} XP
            </span>
            <span>{t('NLSExperienceXpToLevel', { count: Math.max(0, xpNeeded - currentXp), level: level + 1 })}</span>
          </div>
        </div>

        <div className="achHeroMeta">
          <span className="achHeroChip is-streak" title={t('NLSExperienceConsecutiveStreakTitle')}>
            <span className="achHeroChipIcon"><LocalFireDepartmentRoundedIcon fontSize="inherit" /></span>
            {t('NLSExperienceDayStreak', { count: streak })}
          </span>
          {nextUnlock ? (
            <span className="achHeroChip is-next" title={t('NLSExperienceNextClaimableTitle', { title: nextUnlock.title })}>
              <span className="achHeroChipIcon"><AutoAwesomeRoundedIcon fontSize="inherit" /></span>
              {t('NLSExperienceReadyTitle', { title: nextUnlock.title })}
            </span>
          ) : null}
        </div>
      </div>
    </motion.section>
  )
}

// ---------------------------------------------------------------------------
// Spin Vault Capsule
// ---------------------------------------------------------------------------

const SpinVaultCapsule = ({
  tier,
  tickets,
  cursed,
  isHot,
  onSpin,
  t,
}: {
  tier: SpinTier
  tickets: number
  cursed: boolean
  isHot: boolean
  onSpin: () => void
  t: ExperienceNlsT
}) => {
  const { minCredits, maxCredits } = SPIN_TIERS[tier]
  const Icon = TIER_ICON[tier]
  const tierLabel = getSpinTierLabel(tier, t)
  const disabled = tickets <= 0 || cursed
  const state: DisplayCardState | undefined = cursed ? 'frozen' : tickets <= 0 ? 'unavailable' : isHot ? 'hot' : undefined

  return (
    <DisplayCard
      variant="action"
      accent={TIER_ACCENT[tier]}
      state={state}
      disabled={disabled}
      icon={<span className="achVaultGlyph" aria-hidden="true"><Icon fontSize="inherit" /></span>}
      title={<span className="achVaultLabel">{t('NLSExperienceSpinLabel', { tier: tierLabel })}</span>}
      body={(
        <>
          <span className="achVaultRange">{minCredits}–{maxCredits} cr</span>
          <span className={`achVaultTicketsRow ${tickets <= 0 ? 'is-empty' : ''}`}>
            {tickets} <ConfirmationNumberRoundedIcon fontSize="inherit" />
          </span>
        </>
      )}
      footer={(
        <Button
          variant="primary"
          size="sm"
          disabled={disabled}
          className={`achVaultCta ${!disabled ? 'is-ready' : ''}`}
          title={cursed ? t('NLSExperienceSpinFrozenTitle') : undefined}
          onClick={onSpin}
        >
          {cursed ? t('NLSExperienceSpinFrozen') : tickets > 0 ? t('NLSExperienceSpinAction') : t('NLSExperienceSpinNoTickets')}
        </Button>
      )}
    />
  )
}

// ---------------------------------------------------------------------------
// Track Card
// ---------------------------------------------------------------------------

interface TrackCardProps {
  trackId: ActivityTrackId
  count: number
  unclaimed: Partial<Record<SpinTier, number>>
  featured?: boolean
  onCollect: (trackId: ActivityTrackId) => Promise<unknown>
  t: ExperienceNlsT
}

const TrackCard = ({ trackId, count, unclaimed, featured = false, onCollect, t }: TrackCardProps) => {
  const def = ACTIVITY_TRACKS[trackId]
  const level = resolveTrackLevel(count, def.tiers)
  const isMaxed = level >= def.tiers.length
  const nextThreshold = isMaxed ? def.tiers[def.tiers.length - 1] : def.tiers[level]
  const lockedTotal = lockedTicketsCount(unclaimed)
  const lockedSummary = summarizeLockedTickets(unclaimed, t)
  const isClaimable = lockedTotal > 0
  const rarity = rarityForLevel(level)
  const nextTier = nextRewardTier(trackId, level)
  const Icon = TRACK_ICON[def.iconKey]

  const [isClaiming, setIsClaiming] = useState(false)
  const [burstKey, setBurstKey] = useState(0)

  const handleCollect = async () => {
    if (!isClaimable || isClaiming) return
    setIsClaiming(true)
    setBurstKey((k) => k + 1)
    try {
      await onCollect(trackId)
    } finally {
      setIsClaiming(false)
    }
  }

  const states: DisplayCardState[] = [
    isMaxed ? 'maxed' : 'default',
    isClaimable ? 'claimable' : 'default',
    featured ? 'featured' : 'default',
  ]

  return (
    <DisplayCard
      variant="tracker"
      accent={`var(--track-${def.archetype})`}
      state={states}
      style={{ ['--track-accent' as string]: `var(--track-${def.archetype})` }}
      icon={(
        <div className="achTrackEmblem" aria-hidden="true">
          <Icon fontSize="inherit" />
        </div>
      )}
      title={<span className="achTrackTitle">{def.title}</span>}
      subtitle={<span className="achTrackUnit">{def.unitLabel}</span>}
      badge={isMaxed ? (
          <span className="achAscendedTag" title={t('NLSExperienceEveryTierUnlocked')}>
            <StarsRoundedIcon fontSize="inherit" /> {t('NLSExperienceAscended')}
          </span>
        ) : (
          <span className={`achTierBadge ${RARITY_MODIFIER[rarity]}`}>{t('NLSExperienceTierProgress', { level })}</span>
        )}
      body={(
        <>
          <div className="achTierLadder" role="list">
            {def.tiers.map((threshold, index) => {
              const tierNo = index + 1
              const achieved = tierNo <= level
              const isCurrent = tierNo === level + 1
              const fogged = tierNo > level + 1
              const className = [
                'achTierNode',
                achieved ? 'is-achieved' : '',
                isCurrent ? 'is-current' : '',
                !achieved && !isCurrent ? 'is-locked' : '',
                fogged ? 'is-fog' : '',
              ]
                .filter(Boolean)
                .join(' ')
              return (
                <div
                  key={trackId + threshold + index}
                  role="listitem"
                  className={className}
                  title={fogged ? t('NLSExperienceTierLockedTitle') : t('NLSExperienceTierThresholdTitle', { tier: tierNo, threshold, unit: def.unitLabel })}
                >
                  {fogged ? <LockRoundedIcon fontSize="inherit" /> : threshold}
                </div>
              )
            })}
          </div>

          <div className="achTrackRail" role="progressbar" aria-valuemin={0} aria-valuemax={nextThreshold} aria-valuenow={Math.min(count, nextThreshold)}>
            <motion.div
              className="achTrackRailFill"
              initial={false}
              animate={{ width: `${isMaxed ? 100 : pct(count, nextThreshold)}%` }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </>
      )}
      footer={(
        <div className="achTrackFooter">
          <span className="achTrackProgressText">
            {isMaxed ? (
              <>
                {t('NLSExperienceFullyAscended', { count })}
              </>
            ) : (
              <>
                <strong>{count}</strong> / {nextThreshold} {def.unitLabel.toLowerCase()}
              </>
            )}
          </span>
          {!isMaxed && nextTier ? (
            <span className="achTrackNextReward">
              {t('NLSExperienceNext')}
              <span className={`achNextRewardChip ${RARITY_MODIFIER[SPIN_TIER_RARITY[nextTier]]}`}>
                +1 <ConfirmationNumberRoundedIcon fontSize="inherit" /> {getSpinTierLabel(nextTier, t)}
              </span>
            </span>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            className="achClaimButton"
            onClick={() => void handleCollect()}
            disabled={!isClaimable || isClaiming}
            title={isClaimable ? t('NLSExperienceMoveTicketsTitle', { summary: lockedSummary }) : t('NLSExperienceEarnTicketsTitle')}
          >
            {isClaiming ? t('NLSExperienceCollecting') : isClaimable ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {t('NLSExperienceCollect')} (<LockedTicketsInline bucket={unclaimed} />)
              </span>
            ) : t('NLSExperienceCollect')}
          </Button>
          <AnimatePresence>
            {burstKey > 0 && isClaiming ? <ClaimBurst key={burstKey} /> : null}
          </AnimatePresence>
        </div>
      )}
    />
  )
}

// ---------------------------------------------------------------------------
// Claim burst FX
// ---------------------------------------------------------------------------

const ClaimBurst = () => {
  const reduced = useReducedMotion()
  if (reduced) return null
  return (
    <span className="achClaimBurst" aria-hidden="true">
      {Array.from({ length: 10 }).map((_, i) => (
        <span
          key={i}
          className="achClaimSpark"
          style={{ ['--a' as string]: `${(i * 360) / 10}deg` }}
        />
      ))}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Milestone row (Retention section — untouched behavior)
// ---------------------------------------------------------------------------

const MilestoneRow = ({ title, value, milestones, t }: { title: string; value: number; milestones: readonly number[]; t: ExperienceNlsT }) => (
  <article className="gTrackCard">
    <div className="gTrackHead">
      <div>
        <strong>{title}</strong>
        <span className="gMuted">{t('NLSExperienceDaysCount', { count: value })}</span>
      </div>
    </div>
    <div className="gMilestoneRow">
      {milestones.map((milestone) => {
        const reached = value >= milestone
        const isNext = !reached && milestones.filter((m) => m <= value).length === milestones.indexOf(milestone)
        return (
          <span key={milestone} className={`gMilestone ${reached ? 'is-reached' : ''} ${isNext ? 'is-next' : ''}`}>
            {milestone}
          </span>
        )
      })}
    </div>
  </article>
)

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

type TrackFilter = 'all' | 'in-progress' | 'claimable' | 'completed' | 'ascended'

interface TrackSnapshot {
  trackId: ActivityTrackId
  count: number
  level: number
  isMaxed: boolean
  unclaimed: Partial<Record<SpinTier, number>>
  lockedTotal: number
}

const filterTracks = (snapshots: TrackSnapshot[], filter: TrackFilter): TrackSnapshot[] => {
  switch (filter) {
    case 'all':
      return snapshots
    case 'claimable':
      return snapshots.filter((s) => s.lockedTotal > 0)
    case 'ascended':
      return snapshots.filter((s) => s.isMaxed)
    case 'completed':
      return snapshots.filter((s) => s.level >= 4)
    case 'in-progress':
      return snapshots.filter((s) => !s.isMaxed && s.count > 0)
    default:
      return snapshots
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AchievementsPage = () => {
  const { t } = useNls()
  const { profile, isLoading, isWorkspaceCursed, claimTrack } = useGamification()
  const [spinTier, setSpinTier] = useState<SpinTier | null>(null)
  const [filter, setFilter] = useState<TrackFilter>('all')

  const snapshots = useMemo<TrackSnapshot[]>(() => {
    if (!profile) return []
    return ACTIVITY_TRACK_IDS.map((trackId) => {
      const def = ACTIVITY_TRACKS[trackId]
      const count = profile.trackProgress[trackId]?.currentCount ?? 0
      const level = resolveTrackLevel(count, def.tiers)
      const unclaimed = profile.unclaimedTrackTickets[trackId] ?? {}
      return {
        trackId,
        count,
        level,
        isMaxed: level >= def.tiers.length,
        unclaimed,
        lockedTotal: lockedTicketsCount(unclaimed),
      }
    })
  }, [profile])

  const counts = useMemo(
    () => ({
      all: snapshots.length,
      'in-progress': snapshots.filter((s) => !s.isMaxed && s.count > 0).length,
      claimable: snapshots.filter((s) => s.lockedTotal > 0).length,
      completed: snapshots.filter((s) => s.level >= 4).length,
      ascended: snapshots.filter((s) => s.isMaxed).length,
    }),
    [snapshots],
  )

  const featured = useMemo(() => {
    const withUnclaimed = snapshots.filter((s) => s.lockedTotal > 0)
    if (withUnclaimed.length === 0) return null
    return withUnclaimed.reduce((best, cur) => (cur.lockedTotal > best.lockedTotal ? cur : best), withUnclaimed[0])
  }, [snapshots])

  const nextUnlock = useMemo(() => {
    if (!featured) return null
    const highestTier = (['GIGA_SPIN', 'MEGA_SPIN', 'LUCKY_SPIN'] as SpinTier[]).find(
      (tier) => (featured.unclaimed[tier] ?? 0) > 0,
    )
    if (!highestTier) return null
    return { title: `${ACTIVITY_TRACKS[featured.trackId].title} · ${getSpinTierLabel(highestTier, t)} ticket`, tier: highestTier }
  }, [featured, t])

  const filteredSnapshots = useMemo(() => filterTracks(snapshots, filter), [snapshots, filter])
  const gridSnapshots = useMemo(
    () => (featured ? filteredSnapshots.filter((s) => s.trackId !== featured.trackId) : filteredSnapshots),
    [filteredSnapshots, featured],
  )

  const handleCollect = async (trackId: ActivityTrackId) => {
    await claimTrack(trackId)
  }

  if (isLoading || !profile) {
    return (
      <div className="gTabBody achV2">
        <div className="gPad appInlineLoadingState">
          <Loader size={140} label={t('NLSExperienceLoadingProgression')} />
        </div>
      </div>
    )
  }

  const filterChips: ReactNode = (['all', 'in-progress', 'claimable', 'completed', 'ascended'] as TrackFilter[]).map((key) => (
    <FilterChip
      key={key}
      selectionRole="tab"
      selected={filter === key}
      count={counts[key]}
      onClick={() => setFilter(key)}
    >
      {getTrackFilterLabel(key, t)}
    </FilterChip>
  ))

  return (
    <div className="gTabBody achV2">
      <ProgressionHero
        level={profile.level}
        currentXp={profile.currentXp}
        xpNeeded={profile.xpNeededForNextLevel}
        streak={profile.consecutiveLoginStreak}
        equippedTitle={profile.equippedTitle}
        nextUnlock={nextUnlock}
        t={t}
      />

      <section className="achSection">
        <div className="achSectionHeadRow">
          <h2 className="gSectionTitle">{t('NLSExperienceSpinVaultTitle')}</h2>
        </div>
        {isWorkspaceCursed && (
          <div className="gCurseBanner" role="status">
            <GavelRoundedIcon fontSize="inherit" /> <strong>{t('NLSExperienceBossCurseActive')}</strong> {t('NLSExperienceBossCurseBody')}
          </div>
        )}
        <div className="gVaultGrid">
          {(['LUCKY_SPIN', 'MEGA_SPIN', 'GIGA_SPIN'] as SpinTier[]).map((tier) => {
            const field = SPIN_TIERS[tier].ticketField
            const tickets = profile[field]
            return (
              <SpinVaultCapsule
                key={tier}
                tier={tier}
                tickets={tickets}
                cursed={isWorkspaceCursed}
                isHot={tier === 'GIGA_SPIN' && tickets > 0}
                onSpin={() => setSpinTier(tier)}
                t={t}
              />
            )
          })}
        </div>
      </section>

      <section className="achSection">
        <div className="achSectionHeadRow">
          <h2 className="gSectionTitle">{t('NLSExperienceMasteryTracksTitle')}</h2>
          <div className="achFilterRow" role="tablist" aria-label={t('NLSExperienceMasteryTrackFilterAria')}>
            {filterChips}
          </div>
        </div>
        <div className="gTrackGrid">
          {featured && (filter === 'all' || filter === 'claimable' || filter === 'in-progress') ? (
            <TrackCard
              key={`featured-${featured.trackId}`}
              trackId={featured.trackId}
              count={featured.count}
              unclaimed={featured.unclaimed}
              featured
              onCollect={handleCollect}
              t={t}
            />
          ) : null}
          <AnimatePresence initial={false}>
            {gridSnapshots.map((s) => (
              <TrackCard
                key={s.trackId}
                trackId={s.trackId}
                count={s.count}
                unclaimed={s.unclaimed}
                onCollect={handleCollect}
                t={t}
              />
            ))}
          </AnimatePresence>
        </div>
      </section>

      <section className="gSection">
        <h2 className="gSectionTitle">{t('NLSExperienceRetentionTitle')}</h2>
        <div className="gTrackGrid">
          <MilestoneRow title={t('NLSExperienceTotalLogins')} value={profile.cumulativeLoginsCount} milestones={LOGIN_TOTAL_MILESTONES} t={t} />
          <MilestoneRow title={t('NLSExperienceLoginStreak')} value={profile.consecutiveLoginStreak} milestones={LOGIN_STREAK_MILESTONES} t={t} />
        </div>
      </section>

      <SpinWheelModal isOpen={spinTier !== null} tier={spinTier} onClose={() => setSpinTier(null)} />
    </div>
  )
}
